import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Paperclip, Beaker, Droplets } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAutoSave } from "@/hooks/use-auto-save";
import { base44 } from "@/api/base44Client";
import PhotoAttachSplash from "@/components/drill/PhotoAttachSplash";
import PhotoGallery from "@/components/drill/PhotoGallery";
import CalcInfo from "@/components/drill/CalcInfo";
import TimeScroller from "@/components/ui/time-scroller";

import { safeLocalStorage, safeSessionStorage } from '@/lib/browserStorage';
export default function DrillRunForm({ onSubmit, onAutoSave, initialData, defaultRunNumber, defaultFromDepth, defaultStartStickUp, barrelTableHeight, runSize, sheetId, defaultDriller, holeType, conventionalMethod, currentDepth }) {
  const rodLength = Number(runSize || 6);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [splashOpen, setSplashOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const DRAFT_KEY = `drilllog:draft:run:${sheetId ?? "unknown"}:${initialData?.id ?? "new"}`;
  const TOOL_RESULT_KEY = `drilllog:tool-result:${initialData?.id ?? "new"}`;
  const nowTime = () => new Date().toTimeString().slice(0, 5);

  const goMeasureViscosity = () => {
    if (holeType === "Conventional" || !initialData?.id || !sheetId) return;
    try { safeSessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch (e) {}
    const from = encodeURIComponent(`/run-sheet/${sheetId}`);
    navigate(`/tools/viscosity-check?runId=${initialData.id}&from=${from}`);
  };

  const goMeasureFlowRate = () => {
    if (!initialData?.id || !sheetId) return;
    try { safeSessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch (e) {}
    const from = encodeURIComponent(`/run-sheet/${sheetId}`);
    navigate(`/tools/flow-rate?runId=${initialData.id}&from=${from}`);
  };

  useEffect(() => {
    if (initialData) {
      let restored = null;
      let toolResult = null;
      try {
        const raw = safeSessionStorage.getItem(DRAFT_KEY);
        if (raw) restored = JSON.parse(raw);
        const resultRaw = safeSessionStorage.getItem(TOOL_RESULT_KEY);
        if (resultRaw) toolResult = JSON.parse(resultRaw);
      } catch (e) {}
      const merged = { ...initialData, start_time: initialData.start_time || nowTime(), ...(restored || {}), ...(toolResult || {}) };
      setForm(merged);
      if (restored || toolResult) {
        try {
          safeSessionStorage.removeItem(DRAFT_KEY);
          safeSessionStorage.removeItem(TOOL_RESULT_KEY);
        } catch (e) {}
      }
    } else {
      setForm({
        date: new Date().toISOString().split("T")[0],
        run_number: defaultRunNumber || 1,
        from_depth: defaultFromDepth ?? 0,
        rods_in_hole: null,
        stick_up: defaultStartStickUp ?? null,
        end_stick_up: null,
        driller: defaultDriller || "",
        photos: [],
        azimuth: null,
        start_time: nowTime(),
        end_time: "",
      });
    }
  }, [initialData, defaultRunNumber, DRAFT_KEY, TOOL_RESULT_KEY]);

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  // Restore unsaved draft (create mode) on mount
  useEffect(() => {
    if (initialData || !sheetId) return;
    try {
      const raw = safeLocalStorage.getItem(DRAFT_KEY);
      if (raw) setForm(JSON.parse(raw));
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-calculated To Depth (drilled to) = from + (start stick up − end stick up)
  const toDepth = useMemo(() => {
    if (form.from_depth == null || form.stick_up == null || form.end_stick_up == null) return null;
    const val = (+form.from_depth) + ((+form.stick_up) - (+form.end_stick_up));
    return Number.isFinite(val) ? +val.toFixed(2) : null;
  }, [form.from_depth, form.stick_up, form.end_stick_up]);

  // Auto-calculated Rods in Hole: ceil((to depth - barrel-table height) / rod length)
  const rodsInHole = useMemo(() => {
    if (toDepth == null) return null;
    const val = Math.ceil((toDepth - (barrelTableHeight || 0)) / rodLength);
    return Number.isFinite(val) ? val : null;
  }, [toDepth, barrelTableHeight, rodLength]);

  // Auto-calculated Drilled length (to − from)
  const drilled = useMemo(() => {
    if (toDepth == null || form.from_depth == null) return null;
    const val = toDepth - (form.from_depth || 0);
    return Number.isFinite(val) ? +val.toFixed(2) : null;
  }, [toDepth, form.from_depth]);

  // Conventional Core split-rod display: when the current rod reaches 0 stick-up
  // before 4.5 m, expose the next physical rod using the actual string count.
  const splitCoreRun = useMemo(() => (
    holeType === "Conventional" &&
    conventionalMethod === "Core" &&
    Number(form.end_stick_up) === 0 &&
    Number.isFinite(Number(drilled)) &&
    Number(drilled) < 4.5 &&
    (4.5 - Number(drilled)) > 0
  ), [holeType, conventionalMethod, form.end_stick_up, drilled]);

  // A completed split Core run must retain its true total depth even after the
  // Conventional method is changed to Chip. The method toggle changes how new
  // entries are created, but it must not change the saved depth of an older run.
  const savedSplitCoreRun = useMemo(() => (
    holeType === "Conventional" &&
    !!initialData &&
    (initialData.rod2_number != null ||
      initialData.rod2_end_stick_up != null ||
      initialData.rod2_drilled != null)
  ), [holeType, initialData]);

  const hasSplitCoreData = splitCoreRun || savedSplitCoreRun;

  const fallbackRodNumber = useMemo(() => {
    const depth = Number(currentDepth ?? form.from_depth);
    const table = Number(barrelTableHeight || 0);
    const length = Number(rodLength);
    if (!Number.isFinite(depth) || !Number.isFinite(table) || !Number.isFinite(length) || length <= 0) return 1;
    const n = Math.ceil((depth - table) / length);
    return Math.max(1, Number.isFinite(n) ? n : 1);
  }, [currentDepth, form.from_depth, barrelTableHeight, rodLength]);

  const rod1Number = useMemo(() => {
    const n = Number(rodsInHole);
    if (Number.isFinite(n) && n > 0) return n;
    const existing = Number(form.rods_in_hole);
    if (Number.isFinite(existing) && existing > 0) return existing;
    return fallbackRodNumber;
  }, [rodsInHole, form.rods_in_hole, fallbackRodNumber]);

  const rod2Number = rod1Number != null ? rod1Number + 1 : null;

  const rod2Drilled = useMemo(() => {
    const endStickUp = Number(form.rod2_end_stick_up);
    if (Number.isFinite(endStickUp)) {
      const value = Number(rodLength) - endStickUp;
      return Number.isFinite(value) && value >= 0 ? +value.toFixed(2) : 0;
    }
    const value = Number(form.rod2_drilled);
    return Number.isFinite(value) ? +value.toFixed(2) : 0;
  }, [form.rod2_end_stick_up, form.rod2_drilled, rodLength]);

  const totalDrilled = useMemo(() => {
    if (drilled == null) return null;
    return +(Number(drilled) + (hasSplitCoreData ? rod2Drilled : 0)).toFixed(2);
  }, [drilled, hasSplitCoreData, rod2Drilled]);

  // For a split Conventional Core run, older saved records may have to_depth
  // stored as the first rod's depth. Always reconstruct the actual total depth
  // from both rods, including when the sheet has since been changed to Chip.
  const actualToDepth = useMemo(() => {
    if (form.from_depth == null || totalDrilled == null) return toDepth;
    if (!hasSplitCoreData) return toDepth;
    const value = Number(form.from_depth) + Number(totalDrilled);
    return Number.isFinite(value) ? +value.toFixed(2) : toDepth;
  }, [form.from_depth, totalDrilled, hasSplitCoreData, toDepth]);

  // Auto-calculations
  const gainLoss = useMemo(() => {
    if (form.recovered == null || totalDrilled == null) return null;
    return +(form.recovered - totalDrilled).toFixed(2);
  }, [form.recovered, totalDrilled]);

  const rodsRemaining = useMemo(() => {
    if (rodsInHole == null || form.total_rod_count == null) return null;
    const val = form.total_rod_count - rodsInHole;
    return Number.isFinite(val) ? val : null;
  }, [rodsInHole, form.total_rod_count]);

  // Auto-calculated total drill time (end − start)
  const drilledTime = useMemo(() => {
    if (!form.start_time || !form.end_time) return null;
    const [sh, sm] = form.start_time.split(":").map(Number);
    const [eh, em] = form.end_time.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60; // overnight
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  }, [form.start_time, form.end_time]);

  const buildPayload = () => ({
    run_number: form.run_number,
    from_depth: form.from_depth,
    to_depth: actualToDepth,
    stick_up: form.stick_up,
    end_stick_up: form.end_stick_up,
    ...(hasSplitCoreData ? {
      rod2_drilled: rod2Drilled,
      rod2_end_stick_up: form.rod2_end_stick_up,
      rod2_number: form.rod2_number ?? rod2Number,
    } : {}),
    recovered: form.recovered,
    rods_in_hole: rodsInHole,
    total_rod_count: form.total_rod_count,
    rods_remaining: rodsRemaining,
    gain_loss: gainLoss,
    driller: form.driller,
    date: form.date,
    start_time: form.start_time,
    end_time: form.end_time,
    lithology: form.lithology,
    ...(holeType !== "Conventional" ? { viscosity_check: form.viscosity_check } : {}),
    flow_rate: form.flow_rate,
    azimuth: form.azimuth,
    remarks: form.remarks,
    photos: form.photos ?? [],
  });

  // Auto-save every 1 minute: existing run → server, new run → local draft
  useAutoSave(true, () => {
    if (initialData) onAutoSave?.(buildPayload());
    else if (sheetId) {
      try { safeLocalStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch (e) {}
    }
  });

  const handleAttach = async (file) => {
    setUploading(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setForm((p) => ({ ...p, photos: [...(p.photos ?? []), file_url] }));
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = (idx) =>
    setForm((p) => ({ ...p, photos: (p.photos ?? []).filter((_, i) => i !== idx) }));

  const doSubmit = async (close) => {
    setSubmitting(true);
    try {
      await onSubmit(buildPayload(), { close });
      if (!initialData && sheetId) {
        try { safeLocalStorage.removeItem(DRAFT_KEY); } catch (e) {}
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    doSubmit(false);
  };

  const handleSaveAndExit = () => doSubmit(true);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Run Number *</Label>
          <Input
            readOnly
            value={form.run_number ?? ""}
            className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400"
          />
          <p className="text-xs text-slate-400 mt-1">Auto-incremented</p>
        </div>
        <div>
          <Label>Date *</Label>
          <Input
            required
            type="date"
            value={form.date || ""}
            onChange={(e) => set("date", e.target.value)}
            className="rounded-xl"
          />
        </div>
      </div>

      {/* Driller */}
      <div>
        <Label>Driller</Label>
        <Input
          value={form.driller || ""}
          onChange={(e) => set("driller", e.target.value)}
          className="rounded-xl"
        />
      </div>

      {/* Start | Finish | Drilled Time */}
      <div className="grid grid-cols-3 gap-1">
        <div className="min-w-0">
          <Label className="text-xs">Start Time</Label>
          <TimeScroller value={form.start_time || ""} onChange={(value) => set("start_time", value)} className="mt-1" />
        </div>
        <div className="min-w-0">
          <Label className="text-xs">Finish Time</Label>
          <TimeScroller value={form.end_time || ""} onChange={(value) => set("end_time", value)} className="mt-1" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <Label className="text-xs">Drilled Time</Label>
            <CalcInfo title="Drilled Time">
              End time minus start time. Runs crossing midnight wrap to the next day.
            </CalcInfo>
          </div>
          <Input
            readOnly
            value={drilledTime ?? ""}
            className="mt-1 rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400"
          />
        </div>
      </div>

      {/* From Depth | To Depth */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>From Depth (m) *</Label>
          <Input
            required
            type="number"
            step="0.01"
            value={form.from_depth ?? ""}
            onChange={(e) => set("from_depth", e.target.value === "" ? undefined : +e.target.value)}
            className="rounded-xl"
          />
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <Label>To Depth (m)</Label>
            <CalcInfo title="To Depth">
              For a split Conventional Core run, To Depth includes the drilled metres
              from both rods. Otherwise it is from depth + (start stick up − end stick up).
            </CalcInfo>
          </div>
          <Input
            readOnly
            value={actualToDepth ?? ""}
            className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400"
          />
        </div>
      </div>

      {/* Start Stick Up | End Stick Up */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Start Stick Up (m)</Label>
          <Input
            type="number"
            step="0.01"
            value={form.stick_up ?? ""}
            onChange={(e) => set("stick_up", e.target.value === "" ? undefined : +e.target.value)}
            className="rounded-xl"
          />
        </div>
        <div>
          <Label>End Stick Up (m) — Rod {rod1Number}</Label>
          <Input
            type="number"
            step="0.01"
            value={form.end_stick_up ?? ""}
            onChange={(e) => set("end_stick_up", e.target.value === "" ? undefined : +e.target.value)}
            className="rounded-xl"
          />
        </div>
      </div>

      {splitCoreRun && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          <div>Mark {(4.5 - Number(drilled)).toFixed(2)} m from the pin</div>
          <div>Mark {(rodLength - (4.5 - Number(drilled))).toFixed(2)} m from the box</div>
        </div>
      )}

      {/* Drilled values / conditional stick-up / Total Drilled */}
      {splitCoreRun ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <Label>Drilled (m) — Rod {rod1Number ?? 1}</Label>
              <CalcInfo title="Drilled length">To depth − from depth. Drilled metres for the first rod.</CalcInfo>
            </div>
            <Input
              readOnly
              value={drilled ?? ""}
              className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400"
            />
            <div className="mt-2">
              <Label>Drilled (m) — Rod {rod2Number}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.rod2_end_stick_up != null ? rod2Drilled : (form.rod2_drilled ?? "")}
                readOnly
                className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400"
              />
            </div>
          </div>

          <div className="min-w-0">
            <Label>End Stick Up (m) — Rod {rod2Number}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.rod2_end_stick_up ?? ""}
              onChange={(e) => set("rod2_end_stick_up", e.target.value === "" ? undefined : +e.target.value)}
              className="rounded-xl"
            />
            <div className="mt-2">
              <Label>Total Drilled (m)</Label>
              <Input
                readOnly
                value={totalDrilled ?? ""}
                className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="flex items-center gap-1.5">
              <Label>Drilled (m) — Rod {rod1Number ?? 1}</Label>
              <CalcInfo title="Drilled length">To depth − from depth. Total metres drilled during this run.</CalcInfo>
            </div>
            <Input readOnly value={drilled ?? ""} className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400" />
          </div>
          <div>
            <Label>Recovered (m)</Label>
            <Input type="number" step="0.01" value={form.recovered ?? ""} onChange={(e) => set("recovered", e.target.value === "" ? undefined : +e.target.value)} className="rounded-xl" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <Label>Gain/Loss (m)</Label>
              <CalcInfo title="Gain/Loss">Recovered − drilled length. Positive = core gain, negative = core loss.</CalcInfo>
            </div>
            <Input readOnly value={gainLoss ?? ""} className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400" />
          </div>
        </div>
      )}

      {splitCoreRun && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Recovered (m)</Label>
            <Input type="number" step="0.01" value={form.recovered ?? ""} onChange={(e) => set("recovered", e.target.value === "" ? undefined : +e.target.value)} className="rounded-xl" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <Label>Gain/Loss (m)</Label>
              <CalcInfo title="Gain/Loss">Recovered − total drilled. Positive = core gain, negative = core loss.</CalcInfo>
            </div>
            <Input readOnly value={gainLoss ?? ""} className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400" />
          </div>
        </div>
      )}

      {/* Rods in Hole | Total Rod Count | Rods Remaining */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="flex items-center gap-1.5">
            <Label>Rods in Hole</Label>
            <CalcInfo title="Rods in Hole">
              Round up of (to depth − barrel-table height) ÷ rod length (6 m).
              Estimates how many rods are currently down the hole.
            </CalcInfo>
          </div>
          <Input
            readOnly
            value={rodsInHole ?? ""}
            className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400"
          />
        </div>
        <div>
          <Label>Total Rod Count</Label>
          <Input
            type="number"
            step="1"
            value={form.total_rod_count ?? ""}
            onChange={(e) => set("total_rod_count", e.target.value === "" ? undefined : +e.target.value)}
            className="rounded-xl"
          />
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <Label>Rods Remaining</Label>
            <CalcInfo title="Rods Remaining">
              Total rod count − rods in hole. How many rods are left available.
            </CalcInfo>
          </div>
          <Input
            readOnly
            value={rodsRemaining ?? ""}
            className="rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {holeType !== "Conventional" && (
          <div className="rounded-xl border p-3">
            <Label>Viscosity check (s)</Label>
            <Input type="number" step="0.1" value={form.viscosity_check ?? ""} onChange={(e) => set("viscosity_check", e.target.value === "" ? undefined : +e.target.value)} placeholder="Seconds" className="rounded-xl mt-1 text-lg font-semibold" />
            <Button type="button" variant="outline" onClick={goMeasureViscosity} disabled={!initialData?.id} className="rounded-xl gap-2 w-full mt-2"><Beaker className="w-4 h-4"/> Measure</Button>
          </div>
        )}
        <div className="rounded-xl border p-3">
          <Label>Flow rate check (L/min)</Label>
          <Input type="number" step="0.1" value={form.flow_rate ?? ""} onChange={(e) => set("flow_rate", e.target.value === "" ? undefined : +e.target.value)} placeholder="L/min" className="rounded-xl mt-1 text-lg font-semibold" />
          <Button type="button" variant="outline" onClick={goMeasureFlowRate} disabled={!initialData?.id} className="rounded-xl gap-2 w-full mt-2"><Droplets className="w-4 h-4"/> Measure</Button>
        </div>
      </div>
      <div className={holeType === "Diamond" ? "grid grid-cols-2 gap-4" : ""}>
        <div><Label>Lithology</Label><Input value={form.lithology || ""} onChange={(e)=>set("lithology",e.target.value)} className="rounded-xl"/></div>
        {holeType === "Diamond" && <div><Label>Azimuth</Label><Input type="number" step="0.1" value={form.azimuth ?? ""} onChange={(e)=>set("azimuth",e.target.value===""?undefined:+e.target.value)} className="rounded-xl"/></div>}
      </div>

      <div>
        <Label>Remarks</Label>
        <Textarea
          value={form.remarks || ""}
          onChange={(e) => set("remarks", e.target.value)}
          placeholder="Observations, difficulties, equipment notes…"
          className="rounded-xl min-h-[80px]"
        />
      </div>

      {(form.photos ?? []).length > 0 && (
        <div>
          <Label>Attached Photos</Label>
          <PhotoGallery photos={form.photos ?? []} onRemove={handleRemovePhoto} className="mt-2" />
        </div>
      )}

      <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap justify-between items-center gap-2 border-t border-slate-200 bg-background/95 px-1 pt-3 pb-[calc(0.25rem+env(safe-area-inset-bottom))] backdrop-blur dark:border-slate-800">
        <Button
          type="button"
          variant="outline"
          onClick={() => setSplashOpen(true)}
          disabled={uploading}
          className="rounded-xl gap-2"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          Attach photo
        </Button>
        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={submitting}
            variant="outline"
            className="rounded-xl gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Run
          </Button>
          <Button
            type="button"
            onClick={handleSaveAndExit}
            disabled={submitting}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Save and Exit
          </Button>
        </div>
      </div>

      <PhotoAttachSplash open={splashOpen} onOpenChange={setSplashOpen} onPhoto={handleAttach} />
    </form>
  );
}